// src/components/settings/TeamSettingsTab.jsx
import React, { useState } from 'react';
import {
  Users,
  Briefcase,
  Trophy,
  FolderKanban,
  Heart,
  Activity,
  Calendar,
  TrendingUp,
  Clock,
  AlertCircle,
  CheckCircle,
  Info,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Bell,
  Shield
} from 'lucide-react';

export const TeamSettingsTab = ({ formData, onInputChange }) => {
  const [expandedSection, setExpandedSection] = useState('subcategory');

  // Team subcategories
  const teamSubcategories = {
    corporate: {
      label: 'Corporate Team',
      icon: Briefcase,
      color: '#0ea5e9',
      description: 'Business teams with departments, positions, and performance tracking',
      recommendedStatuses: ['wellbeing', 'attendance', 'performance']
    },
    sports: {
      label: 'Sports Team',
      icon: Trophy,
      color: '#f59e0b',
      description: 'Athletic teams with fitness tracking, injuries, and performance metrics',
      recommendedStatuses: ['attendance', 'performance', 'mood']
    },
    project: {
      label: 'Project Team',
      icon: FolderKanban,
      color: '#8b5cf6',
      description: 'Project-based teams with task progress and collaboration tracking',
      recommendedStatuses: ['availability', 'performance', 'wellbeing']
    },
    volunteer: {
      label: 'Volunteer Group',
      icon: Heart,
      color: '#ec4899',
      description: 'Volunteer organizations with hours tracking and contributions',
      recommendedStatuses: ['attendance', 'wellbeing', 'availability']
    }
  };

  // Quick status type definitions
  const quickStatusTypes = {
    wellbeing: {
      label: 'Wellbeing Indicators',
      icon: Heart,
      description: 'Track team member mental and emotional state',
      color: '#ec4899',
      options: [
        { id: 'engaged', label: 'Engaged', icon: '🔥', color: '#10b981' },
        { id: 'stressed', label: 'Stressed', icon: '😰', color: '#ef4444' },
        { id: 'focused', label: 'Focused', icon: '🎯', color: '#3b82f6' },
        { id: 'needsSupport', label: 'Needs Support', icon: '🤝', color: '#f59e0b' },
        { id: 'thriving', label: 'Thriving', icon: '⭐', color: '#8b5cf6' },
        { id: 'overwhelmed', label: 'Overwhelmed', icon: '😓', color: '#dc2626' },
        { id: 'satisfied', label: 'Satisfied', icon: '😊', color: '#059669' },
        { id: 'neutral', label: 'Neutral', icon: '😐', color: '#6b7280' }
      ]
    },
    attendance: {
      label: 'Attendance Tracking',
      icon: Calendar,
      description: 'Monitor daily presence and absences',
      color: '#10b981',
      options: [
        { id: 'present', label: 'Present', icon: '✓', color: '#10b981' },
        { id: 'absent', label: 'Absent', icon: '✗', color: '#ef4444' },
        { id: 'leave', label: 'On Leave', icon: '🏖️', color: '#3b82f6' },
        { id: 'sick', label: 'Sick Leave', icon: '🤒', color: '#f59e0b' },
        { id: 'remote', label: 'Remote', icon: '🏠', color: '#8b5cf6' },
        { id: 'late', label: 'Late', icon: '⏰', color: '#f97316' }
      ]
    },
    performance: {
      label: 'Performance Rating',
      icon: TrendingUp,
      description: 'Quick performance assessments',
      color: '#3b82f6',
      options: [
        { id: 'needs_improvement', label: 'Needs Improvement', icon: '📉', color: '#ef4444' },
        { id: 'below_expectations', label: 'Below Expectations', icon: '👎', color: '#f59e0b' },
        { id: 'meets_expectations', label: 'Meets Expectations', icon: '👍', color: '#10b981' },
        { id: 'exceeds_expectations', label: 'Exceeds Expectations', icon: '🌟', color: '#3b82f6' },
        { id: 'outstanding', label: 'Outstanding', icon: '🏆', color: '#8b5cf6' }
      ]
    },
    mood: {
      label: 'Team Mood',
      icon: Activity,
      description: 'General mood and energy levels',
      color: '#f59e0b',
      options: [
        { id: 'energetic', label: 'Energetic', icon: '⚡', color: '#f59e0b' },
        { id: 'calm', label: 'Calm', icon: '🧘', color: '#10b981' },
        { id: 'tired', label: 'Tired', icon: '😴', color: '#6b7280' },
        { id: 'motivated', label: 'Motivated', icon: '💪', color: '#3b82f6' },
        { id: 'frustrated', label: 'Frustrated', icon: '😤', color: '#ef4444' }
      ]
    },
    availability: {
      label: 'Availability Status',
      icon: Clock,
      description: 'Current availability for tasks',
      color: '#8b5cf6',
      options: [
        { id: 'available', label: 'Available', icon: '🟢', color: '#10b981' },
        { id: 'busy', label: 'Busy', icon: '🔴', color: '#ef4444' },
        { id: 'meeting', label: 'In Meeting', icon: '📅', color: '#f59e0b' },
        { id: 'away', label: 'Away', icon: '⏸️', color: '#6b7280' },
        { id: 'dnd', label: 'Do Not Disturb', icon: '🚫', color: '#dc2626' }
      ]
    }
  };

  // Record categories by subcategory
  const recordCategoriesByType = {
    corporate: [
      { id: 'attendance', label: 'Attendance', icon: '📅', color: '#10b981' },
      { id: 'performance', label: 'Performance', icon: '⭐', color: '#3b82f6' },
      { id: 'behavior', label: 'Behavior', icon: '😊', color: '#8b5cf6' },
      { id: 'achievement', label: 'Achievement', icon: '🏆', color: '#f59e0b' },
      { id: 'concern', label: 'Concern', icon: '⚠️', color: '#ef4444' },
      { id: 'goal', label: 'Goal', icon: '🎯', color: '#06b6d4' },
      { id: 'feedback', label: 'Feedback', icon: '💬', color: '#6366f1' },
      { id: 'training', label: 'Training', icon: '📚', color: '#14b8a6' }
    ],
    sports: [
      { id: 'attendance', label: 'Attendance', icon: '📅', color: '#10b981' },
      { id: 'performance', label: 'Performance', icon: '⭐', color: '#3b82f6' },
      { id: 'fitness', label: 'Fitness', icon: '💪', color: '#059669' },
      { id: 'injury', label: 'Injury', icon: '🤕', color: '#ef4444' },
      { id: 'achievement', label: 'Achievement', icon: '🏆', color: '#f59e0b' },
      { id: 'discipline', label: 'Discipline', icon: '⚖️', color: '#dc2626' },
      { id: 'skill', label: 'Skill Development', icon: '🎯', color: '#8b5cf6' }
    ],
    project: [
      { id: 'attendance', label: 'Attendance', icon: '📅', color: '#10b981' },
      { id: 'performance', label: 'Performance', icon: '⭐', color: '#3b82f6' },
      { id: 'taskProgress', label: 'Task Progress', icon: '✅', color: '#059669' },
      { id: 'collaboration', label: 'Collaboration', icon: '🤝', color: '#8b5cf6' },
      { id: 'issue', label: 'Issue', icon: '⚠️', color: '#ef4444' },
      { id: 'milestone', label: 'Milestone', icon: '🎯', color: '#f59e0b' },
      { id: 'feedback', label: 'Feedback', icon: '💬', color: '#6366f1' }
    ],
    volunteer: [
      { id: 'attendance', label: 'Attendance', icon: '📅', color: '#10b981' },
      { id: 'hours', label: 'Hours Logged', icon: '⏰', color: '#3b82f6' },
      { id: 'contribution', label: 'Contribution', icon: '✨', color: '#8b5cf6' },
      { id: 'achievement', label: 'Achievement', icon: '🏆', color: '#f59e0b' },
      { id: 'appreciation', label: 'Appreciation', icon: '❤️', color: '#ec4899' },
      { id: 'feedback', label: 'Feedback', icon: '💬', color: '#6366f1' }
    ]
  };

  const currentSubcategory = formData.albumSubcategory || 'corporate';
  const currentQuickStatusOptions = formData.quickStatusOptions || {};
  const currentRecordCategories = formData.teamRecordCategories || recordCategoriesByType[currentSubcategory];

  const handleSubcategoryChange = (subcategoryKey) => {
    onInputChange('albumSubcategory', subcategoryKey);
    
    // Auto-enable recommended statuses
    const subcategory = teamSubcategories[subcategoryKey];
    if (subcategory.recommendedStatuses) {
      const newOptions = {};
      subcategory.recommendedStatuses.forEach(statusKey => {
        newOptions[statusKey] = {
          ...quickStatusTypes[statusKey],
          enabled: true,
          showOnCard: true,
          trackHistory: true,
          requireNotes: false
        };
      });
      onInputChange('quickStatusOptions', newOptions);
    }
    
    // Set default record categories
    onInputChange('teamRecordCategories', recordCategoriesByType[subcategoryKey]);
  };

  const handleStatusToggle = (statusKey) => {
    const currentStatus = currentQuickStatusOptions[statusKey] || {};
    onInputChange(`quickStatusOptions.${statusKey}`, {
      ...quickStatusTypes[statusKey],
      ...currentStatus,
      enabled: !currentStatus.enabled
    });
  };

  const handleStatusSettingChange = (statusKey, setting, value) => {
    const currentStatus = currentQuickStatusOptions[statusKey] || {};
    onInputChange(`quickStatusOptions.${statusKey}`, {
      ...currentStatus,
      [setting]: value
    });
  };

  const SectionToggle = ({ title, isExpanded, onClick }) => (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
    >
      <span className="font-medium text-gray-900">{title}</span>
      <CheckCircle className={`h-5 w-5 ${isExpanded ? 'text-blue-600' : 'text-gray-400'}`} />
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b">
        <div className="p-3 bg-blue-100 rounded-lg">
          <Users className="h-6 w-6 text-blue-600" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">Team Management Configuration</h3>
          <p className="text-sm text-gray-600">
            Configure how you track and manage your team members
          </p>
        </div>
      </div>

      {/* Team Subcategory Selection */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-semibold text-gray-900">Team Type</h4>
          <span className="text-sm text-gray-500">Step 1 of 4</span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(teamSubcategories).map(([key, subcategory]) => {
            const Icon = subcategory.icon;
            const isSelected = currentSubcategory === key;
            
            return (
              <button
                key={key}
                onClick={() => handleSubcategoryChange(key)}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  isSelected
                    ? 'border-current shadow-md'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                style={{
                  backgroundColor: isSelected ? `${subcategory.color}10` : '#ffffff',
                  borderColor: isSelected ? subcategory.color : undefined
                }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: `${subcategory.color}20` }}
                  >
                    <Icon className="h-5 w-5" style={{ color: subcategory.color }} />
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900">{subcategory.label}</h5>
                    {isSelected && (
                      <span className="text-xs" style={{ color: subcategory.color }}>
                        ✓ Selected
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-600">{subcategory.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Quick Status Options */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-lg font-semibold text-gray-900">Quick Status Buttons</h4>
            <p className="text-sm text-gray-600">
              Enable 1-click status updates that appear on member cards
            </p>
          </div>
          <span className="text-sm text-gray-500">Step 2 of 4</span>
        </div>

        <div className="space-y-3">
          {Object.entries(quickStatusTypes).map(([statusKey, statusType]) => {
            const Icon = statusType.icon;
            const isEnabled = currentQuickStatusOptions[statusKey]?.enabled || false;
            const statusConfig = currentQuickStatusOptions[statusKey] || {};
            
            return (
              <div
                key={statusKey}
                className={`border-2 rounded-lg transition-all ${
                  isEnabled ? 'border-blue-200 bg-blue-50' : 'border-gray-200'
                }`}
              >
                {/* Header */}
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: `${statusType.color}20` }}
                      >
                        <Icon className="h-5 w-5" style={{ color: statusType.color }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h5 className="font-medium text-gray-900">{statusType.label}</h5>
                          {isEnabled && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                              Enabled
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{statusType.description}</p>
                        
                        {/* Status Options Preview */}
                        {isEnabled && (
                          <div className="flex flex-wrap gap-1.5 mt-3">
                            {statusType.options.slice(0, 6).map(option => (
                              <span
                                key={option.id}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
                                style={{
                                  backgroundColor: `${option.color}15`,
                                  color: option.color
                                }}
                              >
                                <span>{option.icon}</span>
                                <span>{option.label}</span>
                              </span>
                            ))}
                            {statusType.options.length > 6 && (
                              <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                                +{statusType.options.length - 6} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Enable Toggle */}
                    <label className="relative inline-flex items-center cursor-pointer ml-3">
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={() => handleStatusToggle(statusKey)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>

                {/* Settings (when enabled) */}
                {isEnabled && (
                  <div className="border-t border-gray-200 bg-white p-4 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {/* Show on Card */}
                      <label className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={statusConfig.showOnCard !== false}
                          onChange={(e) => handleStatusSettingChange(statusKey, 'showOnCard', e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex items-center gap-2">
                          <Eye className="h-4 w-4 text-gray-600" />
                          <span className="text-sm font-medium text-gray-900">Show on Card</span>
                        </div>
                      </label>

                      {/* Track History */}
                      <label className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={statusConfig.trackHistory !== false}
                          onChange={(e) => handleStatusSettingChange(statusKey, 'trackHistory', e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-600" />
                          <span className="text-sm font-medium text-gray-900">Track History</span>
                        </div>
                      </label>

                      {/* Require Notes */}
                      <label className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={statusConfig.requireNotes || false}
                          onChange={(e) => handleStatusSettingChange(statusKey, 'requireNotes', e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-gray-600" />
                          <span className="text-sm font-medium text-gray-900">Require Notes</span>
                        </div>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Record Categories */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-lg font-semibold text-gray-900">Team Record Categories</h4>
            <p className="text-sm text-gray-600">
              Categories for detailed updates in MediaExpressionsModal
            </p>
          </div>
          <span className="text-sm text-gray-500">Step 3 of 4</span>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex flex-wrap gap-2">
            {currentRecordCategories.map(category => (
              <div
                key={category.id}
                className="inline-flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200 shadow-sm"
              >
                <span className="text-lg">{category.icon}</span>
                <span className="text-sm font-medium text-gray-900">{category.label}</span>
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: category.color }}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-medium mb-1">How Record Categories Work</p>
              <p>When recording team updates via MediaExpressionsModal, managers select a category from the dropdown to classify the update (e.g., Attendance, Performance, Behavior). This helps organize and filter team records.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Wellbeing Alerts */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-lg font-semibold text-gray-900">Wellbeing Alerts</h4>
            <p className="text-sm text-gray-600">
              Automatic notifications when concerning patterns are detected
            </p>
          </div>
          <span className="text-sm text-gray-500">Step 4 of 4</span>
        </div>

        <div className="space-y-3">
          {['stressed', 'overwhelmed', 'needsSupport'].map(statusId => {
            const alertConfig = formData.wellbeingAlerts?.[statusId] || { enabled: false, threshold: 3, period: 'week' };
            
            return (
              <div key={statusId} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Bell className="h-5 w-5 text-orange-500" />
                    <span className="font-medium text-gray-900 capitalize">{statusId.replace(/([A-Z])/g, ' $1')}</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={alertConfig.enabled}
                      onChange={(e) => onInputChange(`wellbeingAlerts.${statusId}.enabled`, e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                  </label>
                </div>

                {alertConfig.enabled && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Threshold (occurrences)
                      </label>
                      <input
                        type="number"
                        value={alertConfig.threshold}
                        onChange={(e) => onInputChange(`wellbeingAlerts.${statusId}.threshold`, parseInt(e.target.value))}
                        min="1"
                        max="10"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Period
                      </label>
                      <select
                        value={alertConfig.period}
                        onChange={(e) => onInputChange(`wellbeingAlerts.${statusId}.period`, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="day">Per Day</option>
                        <option value="week">Per Week</option>
                        <option value="month">Per Month</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary Card */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-blue-600" />
          Configuration Summary
        </h4>
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Team Type:</span>
            <span className="font-medium text-gray-900">
              {teamSubcategories[currentSubcategory]?.label || 'Not Selected'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Quick Status Types:</span>
            <span className="font-medium text-gray-900">
              {Object.values(currentQuickStatusOptions).filter(s => s.enabled).length} enabled
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Record Categories:</span>
            <span className="font-medium text-gray-900">
              {currentRecordCategories.length} categories
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Wellbeing Alerts:</span>
            <span className="font-medium text-gray-900">
              {Object.values(formData.wellbeingAlerts || {}).filter(a => a.enabled).length} active
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};